// ---------------------------------------------------------------------------
// Mock data for HubSelectionDemo (PR-EVAL-1-DEMO).
// All data is generic placeholder — no real academies, kids, or selectors.
// Score generation uses a seeded LCG so output is stable across renders.
// ---------------------------------------------------------------------------

export type CriterionKey = string;

export interface Criterion {
  key: CriterionKey;
  label: string;
}

export interface CriteriaCategory {
  category: string;
  color: "blue" | "green" | "purple" | "amber";
  subcategory?: string;
  criteria: Criterion[];
}

export interface Candidate {
  id: number;
  name: string;
  ageGroup: string;
  position: string;
}

export const MOCK_EVALUATORS = ["Selector A", "Selector B", "Selector C"] as const;
export type EvaluatorName = (typeof MOCK_EVALUATORS)[number];

// 23 criteria across 4 categories
export const CRITERIA_GROUPS: CriteriaCategory[] = [
  {
    category: "Core Skills",
    color: "blue",
    subcategory: "Batting",
    criteria: [
      { key: "bat_powerplay", label: "Powerplay Intent" },
      { key: "bat_rotation",  label: "Strike Rotation" },
      { key: "bat_singles",   label: "Inner Circle Singles" },
      { key: "bat_reading",   label: "Field Reading" },
      { key: "bat_partner",   label: "Partnership Building" },
      { key: "bat_surface",   label: "Surface Adaptation" },
    ],
  },
  {
    category: "Core Skills",
    color: "blue",
    subcategory: "Bowling",
    criteria: [
      { key: "bowl_field",    label: "Field Ownership" },
      { key: "bowl_bouncer",  label: "Bouncer Use" },
      { key: "bowl_reading",  label: "Batter Reading" },
      { key: "bowl_spin",     label: "Spin Attack Mindset" },
      { key: "bowl_death",    label: "Death Variation" },
    ],
  },
  {
    category: "Core Skills",
    color: "blue",
    subcategory: "Fielding",
    criteria: [
      { key: "field_catch",   label: "Catch Reliability" },
      { key: "field_throw",   label: "Throw Accuracy" },
      { key: "field_pos",     label: "Positioning" },
    ],
  },
  {
    category: "Athleticism",
    color: "green",
    criteria: [
      { key: "ath_stamina",   label: "Stamina (trial-day)" },
      { key: "ath_agility",   label: "Agility" },
      { key: "ath_reaction",  label: "Reaction Speed" },
    ],
  },
  {
    category: "Match Awareness",
    color: "purple",
    criteria: [
      { key: "iq_flow",       label: "Game Flow Sense" },
      { key: "iq_field",      label: "Field Setting" },
      { key: "iq_adapt",      label: "Situation Adaptation" },
    ],
  },
  {
    category: "Temperament",
    color: "amber",
    criteria: [
      { key: "temp_body",     label: "Body Language" },
      { key: "temp_team",     label: "Team Spirit" },
      { key: "temp_compose",  label: "Composure Under Pressure" },
    ],
  },
];

export const ALL_CRITERIA: Criterion[] = CRITERIA_GROUPS.flatMap((g) => g.criteria);

export const MOCK_CANDIDATES: Candidate[] = [
  // U15 — original cohort
  { id: 1,  name: "Alex Chen",    ageGroup: "U15", position: "Batting All-Rounder" },
  { id: 2,  name: "Jordan Patel", ageGroup: "U15", position: "Fast Bowler" },
  { id: 3,  name: "Sam Williams", ageGroup: "U15", position: "Wicket-Keeper Batter" },
  { id: 4,  name: "Casey Nguyen", ageGroup: "U15", position: "Spin Bowler" },
  { id: 5,  name: "Riley Kumar",  ageGroup: "U15", position: "Batting All-Rounder" },
  { id: 6,  name: "Morgan Singh", ageGroup: "U15", position: "Pace All-Rounder" },
  { id: 7,  name: "Avery Sharma", ageGroup: "U15", position: "Top-Order Batter" },
  { id: 8,  name: "Quinn Ramos",  ageGroup: "U15", position: "Leg Spin Bowler" },
  // U11
  { id: 9,  name: "Blake Torres", ageGroup: "U11", position: "Batting All-Rounder" },
  { id: 10, name: "Drew Iyer",    ageGroup: "U11", position: "Fast Bowler" },
  // U13
  { id: 11, name: "Finn Kapoor",  ageGroup: "U13", position: "Top-Order Batter" },
  { id: 12, name: "Grace Mehta",  ageGroup: "U13", position: "Spin Bowler" },
  // U17
  { id: 13, name: "Hayden Nair",  ageGroup: "U17", position: "Pace All-Rounder" },
  { id: 14, name: "Isla Chandra", ageGroup: "U17", position: "Wicket-Keeper Batter" },
];

// ---------------------------------------------------------------------------
// Seeded LCG — deterministic, same output every call, no Math.random().
// Range: always returns 1-5.
// ---------------------------------------------------------------------------
function seededScore(candidateId: number, evaluatorIdx: number, criterionIdx: number): number {
  const seed = candidateId * 31337 + evaluatorIdx * 1009 + criterionIdx * 97;
  const lcg = ((seed * 1664525 + 1013904223) >>> 0) % 5;
  return lcg + 1; // 1-5
}

// Deliberate disagreement: candidates 3 and 6 on specific criteria get forced spread.
// Variance threshold for the badge is > 1.0 (avg per-criterion variance across 23 criteria).
const FORCED_DISAGREEMENT: Record<number, Record<string, number[]>> = {
  3: {
    bat_powerplay: [2, 5, 3],   // spread = 3
    bat_rotation:  [5, 1, 4],   // spread = 4
    bowl_reading:  [5, 2, 4],   // spread = 3
    bowl_death:    [1, 5, 3],   // spread = 4
    temp_compose:  [1, 4, 3],   // spread = 3
    iq_flow:       [3, 5, 1],   // spread = 4
    ath_agility:   [5, 1, 4],   // spread = 4
    field_pos:     [2, 5, 1],   // spread = 4
  },
  6: {
    bat_rotation:  [1, 4, 5],   // spread = 4
    ath_stamina:   [5, 2, 3],   // spread = 3
    temp_body:     [4, 1, 3],   // spread = 3
    field_catch:   [2, 5, 4],   // spread = 3
  },
};

export type ScoreMap = Record<number, Record<EvaluatorName, Record<CriterionKey, number>>>;

function buildScores(): ScoreMap {
  const scores: ScoreMap = {};
  MOCK_CANDIDATES.forEach((candidate) => {
    scores[candidate.id] = {} as Record<EvaluatorName, Record<CriterionKey, number>>;
    MOCK_EVALUATORS.forEach((evaluator, evalIdx) => {
      scores[candidate.id][evaluator] = {};
      ALL_CRITERIA.forEach((criterion, critIdx) => {
        const forced = FORCED_DISAGREEMENT[candidate.id]?.[criterion.key];
        scores[candidate.id][evaluator][criterion.key] = forced
          ? forced[evalIdx]
          : seededScore(candidate.id, evalIdx, critIdx);
      });
    });
  });
  return scores;
}

export const MOCK_SCORES: ScoreMap = buildScores();

// ---------------------------------------------------------------------------
// Derived aggregates used across tabs
// ---------------------------------------------------------------------------

/** Average score for a candidate+evaluator across all criteria in a category group. */
export function categoryAvg(
  candidateId: number,
  evaluator: EvaluatorName,
  category: string,
): number {
  const groups = CRITERIA_GROUPS.filter((g) => g.category === category);
  const keys = groups.flatMap((g) => g.criteria.map((c) => c.key));
  const vals = keys.map((k) => MOCK_SCORES[candidateId][evaluator][k]);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/** Composite avg across all 23 criteria, all 3 evaluators. */
export function compositeScore(candidateId: number): number {
  const all = MOCK_EVALUATORS.flatMap((ev) =>
    ALL_CRITERIA.map((c) => MOCK_SCORES[candidateId][ev][c.key])
  );
  return all.reduce((a, b) => a + b, 0) / all.length;
}

/** Per-criterion variance across evaluators, then averaged over all criteria.
 *  High value = evaluators disagree. Badge threshold: > 1.0.
 */
export function candidateDisagreementScore(candidateId: number): number {
  const criterionVariances = ALL_CRITERIA.map((c) => {
    const vals = MOCK_EVALUATORS.map((ev) => MOCK_SCORES[candidateId][ev][c.key]);
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    return vals.reduce((sum, v) => sum + (v - mean) ** 2, 0) / vals.length;
  });
  return criterionVariances.reduce((a, b) => a + b, 0) / criterionVariances.length;
}

export const DISAGREEMENT_THRESHOLD = 0.8;

// Unique category names in display order
export const CATEGORY_NAMES = Array.from(
  new Set(CRITERIA_GROUPS.map((g) => g.category))
);
